import { createSelector } from 'reselect'
import memoize from '@doveconviene/sf-web-js-sdk/memoize'
import { formatGeolocation } from 'utils'
import isFromExtendedNetworkUtil from 'utils/isFromExtendedNetwork'
import { selectRouterInitialLocation } from 'flux/selectors/router.selectors'
import { BASIC_THEME_TWO_PAGES_BREAKPOINT_FALLBACK } from 'basicTheme'

export const selectProperty = (state) => state.property;
export const selectPropertyId = (state) => state.property.id;
export const selectPropertyFetched = (state) => state.property.fetched;
export const selectPropertyExtras = (state) => state.property.extras || {};
export const selectPropertyAssets = (state) => state.property.assets || [];

export const selectPropertyMessages = createSelector(
  selectPropertyExtras,
  (extras) => {
    const { messages } = extras;
    return messages || {};
  }
);

export const selectPropertyFBPixels = createSelector(
  selectPropertyExtras,
  (extras) => {
    const { REACT_APP_ENV, REACT_APP_FB_PIXEL_ID } = process.env;
    const pixels =
      REACT_APP_ENV === "dev" || REACT_APP_ENV === "stg"
        ? [REACT_APP_FB_PIXEL_ID]
        : extras.fb_pixels || [];

    return pixels.filter((pixel) => !!pixel);
  }
);

export const selectPropertyGoogleAnalytics = createSelector(
  selectPropertyExtras,
  ({
    google_analytics: googleAnalytics,
    google_analytics_ids: googleAnalyticsIds = [],
  }) => {
    const { REACT_APP_ENV, REACT_APP_GOOGLE_ANALYTICS_ID } = process.env;
    return REACT_APP_ENV === "dev" || REACT_APP_ENV === "stg"
      ? [REACT_APP_GOOGLE_ANALYTICS_ID, ""]
      : [...googleAnalyticsIds, googleAnalytics];
  }
);

export const selectPropertyGoogleAdWords = createSelector(
  selectPropertyExtras,
  (extras) => {
    const { REACT_APP_ENV, REACT_APP_GOOGLE_ADWORDS_ID } = process.env;
    return REACT_APP_ENV === "dev" || REACT_APP_ENV === "stg"
      ? REACT_APP_GOOGLE_ADWORDS_ID
      : extras.google_adwords;
  }
);

export const selectPropertyGoogleAdWordsLabel = createSelector(
  selectPropertyExtras,
  (extras) => {
    const { REACT_APP_ENV, REACT_APP_GOOGLE_ADWORDS_LABEL } = process.env;
    return REACT_APP_ENV === "dev" || REACT_APP_ENV === "stg"
      ? REACT_APP_GOOGLE_ADWORDS_LABEL
      : extras.google_adwords_label;
  }
);

export const selectPropertyGoogleAdWordsPhoneCta = createSelector(
  selectPropertyExtras,
  (extras) => {
    const { REACT_APP_ENV, REACT_APP_GOOGLE_ADWORDS_LABEL } = process.env;
    return REACT_APP_ENV === "dev" || REACT_APP_ENV === "stg"
      ? REACT_APP_GOOGLE_ADWORDS_LABEL
      : extras.google_adwords_phone_call;
  }
);

export const selectPropertyWoosmapPublicKey = createSelector(
  selectPropertyExtras,
  (extras) => {
    const { REACT_APP_ENV, REACT_APP_WOOSMAP_PUBLIC_KEY } = process.env;
    return REACT_APP_ENV === "dev" || REACT_APP_ENV === "stg"
      ? REACT_APP_WOOSMAP_PUBLIC_KEY
      : extras.woosmap_public_key;
  }
);

export const selectPropertyGAWPercentageLabels = createSelector(
  selectPropertyExtras,
  (extras) => {
    const { REACT_APP_ENV } = process.env;
    return REACT_APP_ENV === "dev" || REACT_APP_ENV === "stg"
      ? {
          30: process.env.REACT_APP_GAW_PERCENTAGE_30_LABEL,
          50: process.env.REACT_APP_GAW_PERCENTAGE_50_LABEL,
          80: process.env.REACT_APP_GAW_PERCENTAGE_80_LABEL,
        }
      : {
          30: extras.google_adwords_percentage_30_label,
          50: extras.google_adwords_percentage_50_label,
          80: extras.google_adwords_percentage_80_label,
        };
  }
);

export const selectPropertyGAWPercentageLabel = createSelector(
  [(_, percentage) => percentage, selectPropertyGAWPercentageLabels],
  (percentage, labels) => labels[percentage] || null
);

export const selectPropertyGeolocation = (state) => {
  const { lat, lng, label } = selectPropertyExtras(state).geolocation;

  return lat && lng && label
    ? formatGeolocation({ lat, lng, label }, "property")
    : null;
};

export const selectPropertyCTA = memoize((state, index) => {
  const rawCTA = state.property.ctas[index];
  if (!rawCTA) return null;

  const {
    name,
    title,
    description,
    label,
    href,
    assets,
    is_active, // eslint-disable-line camelcase
  } = rawCTA;

  return {
    name,
    title,
    description,
    button: { label, href },
    assets: assets.reduce(
      (acc, asset) => ({ ...acc, [asset.name]: asset.url }),
      {}
    ),
    is_active,
  };
});

export const selectPropertyExternals = (state, key) =>
  state.property.extras[key] || {
    active: true,
    url: "",
    target: "",
  };

export const selectPropertyEnrichmentPins = createSelector(
  selectPropertyAssets,
  (assets) => {
    const pinCrop = assets.find(({ name }) => name === "pinCrop");
    const pinExternalLink = assets.find(
      ({ name }) => name === "pinExternalLink"
    );

    return {
      crop: pinCrop ? pinCrop.url : null,
      external_link: pinExternalLink ? pinExternalLink.url : null,
    };
  }
);

export const selectPropertyPrivacyUrl = createSelector(
  selectPropertyExtras,
  (extras) => {
    const {
      web_network: {
        privacy_policy_url: privacyPolicyUrl = null,
        privacy_policy_url_label: privacyPolicyUrlLabel = null,
      } = {},
    } = extras;
    return (
      (privacyPolicyUrl &&
        privacyPolicyUrlLabel && {
          label: privacyPolicyUrlLabel,
          url: privacyPolicyUrl,
        }) ||
      null
    );
  }
);

export const selectPropertyCloseCta = createSelector(
  selectPropertyExtras,
  (extras) => {
    const { close_cta: closeCta = null } = extras;
    return closeCta;
  }
);

export const selectPropertyCloseCtaBack = createSelector(
  selectPropertyExtras,
  (extras) => {
    const { close_cta_back: closeCtaBack = false } = extras;

    return Boolean(+closeCtaBack);
  }
);

export const selectIsProfilingConsent = (state) => {
  const { profiling_consent: profilingConsent } = selectPropertyExtras(state);
  return profilingConsent;
};

export const selectPropertyMultiRetailerMode = createSelector(
  selectPropertyExtras,
  (extras) => Boolean(+extras?.multiretailer_mode)
);

export const selectPropertyMultiStoreMode = createSelector(
  selectPropertyExtras,
  (extras) => Boolean(+extras?.multistore_mode)
);

export const selectPropertyEndDateHidden = createSelector(
  selectPropertyExtras,
  (extras) => Boolean(+extras?.hidden_end_date)
);

export const isStoreLocatorActive = createSelector(
  selectPropertyExtras,
  (extras) => Boolean(+extras?.store?.active)
);

export const isFromExtendedNetwork = createSelector(
  selectRouterInitialLocation,
  (location) => isFromExtendedNetworkUtil(location.search)
);

export const selectDataLayerIsActive = createSelector(
  selectPropertyExtras,
  (extras) => Boolean(+extras?.data_layer_is_active)
);

export const selectCtaTextOverride = createSelector(
  selectPropertyExtras,
  (extras) => extras?.cta_text_override
);

export const selectCtaUtmParamsReplacementEnabled = createSelector(
  selectPropertyExtras,
  (extras) => Boolean(+extras?.cta_utm_params_replacement_enabled)
);

export const selectAddToCartCtaEnabled = createSelector(
  selectPropertyExtras,
  (extras) => Boolean(+extras?.cta_add_to_cart_enabled)
);

export const selectAddToCartCtaText = createSelector(
  selectPropertyExtras,
  (extras) => extras?.cta_add_to_cart_text || ""
);

export const selectAddToCartTextColor = createSelector(
  selectPropertyExtras,
  (extras) => extras?.cta_add_to_cart_text_color || "white"
);

export const selectAddToCartBackgroundColor = createSelector(
  selectPropertyExtras,
  extras => extras?.cta_add_to_cart_background_color || 'black',
)

export const selectMinWidthToShowTwoPages = createSelector(
  selectPropertyExtras,
  extras => {
    const breakpointString = extras.two_pages_breakpoint
    if (breakpointString === null || breakpointString === undefined) return BASIC_THEME_TWO_PAGES_BREAKPOINT_FALLBACK
    if (typeof breakpointString === 'string' && breakpointString.trim() === '') return BASIC_THEME_TWO_PAGES_BREAKPOINT_FALLBACK

    const breakpointNumber = Number(breakpointString)

    const isValidNumber = !isNaN(breakpointNumber)
    if (!isValidNumber) return BASIC_THEME_TWO_PAGES_BREAKPOINT_FALLBACK

    return breakpointNumber
  }
)
